import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CommaSeprator from "../components/CommaSeprator";
import {
  isDigitalOrHoardingMedia,
  isMediaProduct,
  isMultiplexMedia,
} from "../utils/adminMediaBuyingPath";
import { calculatePrintMediaTotal } from "../utils/mediaCalculations";

const INVENTORY_LIST_PATH = "/admindashboard/bw-marketplace";

export default function PrintMediaInventoryBuying() {
  const { id: productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [timelineToBuy, setTimelineToBuy] = useState(1);
  const [unitsToBuy, setUnitsToBuy] = useState(1);
  const [boughtSeconds, setBoughtSeconds] = useState(1);

  const isDigitalRoute = location.pathname.includes("digitalAndHoardingMediaBuying");

  const loadProduct = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`product/get_product_byId/${productId}`, {
        withCredentials: true,
      });
      const p = res?.data;
      if (!isMediaProduct(p) || isMultiplexMedia(p)) {
        setError("This listing is not eligible for this media buying page.");
        setProduct(null);
        return;
      }
      if (isDigitalRoute && !isDigitalOrHoardingMedia(p)) {
        setError("This listing is not a Digital/Hoarding media product.");
        setProduct(null);
        return;
      }
      setProduct(p);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load product");
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [productId, isDigitalRoute]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const variant = product?.ProductsVariantions?.[0] || product?.mediaVariation || {};
  const minUnits = Number(variant?.minOrderQuantityunit) > 0 ? Number(variant.minOrderQuantityunit) : 1;
  // availableInsertions is decremented on each BW procurement; fall back to maxOrderQuantityunit
  // for listings that haven't been procured before.
  const availableInsertions =
    Number(variant?.availableInsertions ?? variant?.maxOrderQuantityunit) || 0;
  const exceedsAvailable = availableInsertions > 0 && Number(unitsToBuy) > availableInsertions;

  const totalPrice = useMemo(() => {
    if (!product) return 0;
    let base = calculatePrintMediaTotal(variant, {
      unitsToBebought: Number(unitsToBuy) || 1,
    });
    if (product?.OtherCost?.length) {
      product.OtherCost.forEach((cost) => {
        if (cost?.currencyType !== "BXITokens") return;
        if (cost?.AdCostApplicableOn === "PerUnit") {
          base +=
            (Number(cost?.CostPrice) || 0) *
            (Number(unitsToBuy) || 1)
        } else if (cost?.AdCostApplicableOn === "All") {
          base += Number(cost?.CostPrice) || 0;
        }
      });
    }
    return base;
  }, [product, unitsToBuy]);

  const additionalCostRows = useMemo(() => {
    if (!product?.OtherCost?.length) return [];
    const qtyMultiplier = (Number(unitsToBuy) || 1) * (Number(timelineToBuy) || 1);
    return product.OtherCost.map((cost, idx) => {
      const baseUnit = Number(cost?.CostPrice) || 0;
      const baseAmount =
        cost?.AdCostApplicableOn === "PerUnit" ? baseUnit * qtyMultiplier : baseUnit;
      const gstPct = Number(cost?.AdCostGST) || 0;
      const gstAmount = (baseAmount * gstPct) / 100;
      return {
        key: `${cost?.ReasonOfCost || "cost"}-${idx}`,
        reason: cost?.ReasonOfCost || "Additional charge",
        applicability: cost?.AdCostApplicableOn === "All" ? "One Time" : "Per Unit",
        currencyType: cost?.currencyType || "BXITokens",
        gstPct,
        baseAmount,
        gstAmount,
        totalAmount: baseAmount + gstAmount,
      };
    });
  }, [product, unitsToBuy, timelineToBuy]);

  const handleCreateOrder = async () => {
    if (!product?._id) return;
    if (exceedsAvailable) {
      setError(
        `Cannot purchase ${unitsToBuy} insertion(s). Only ${availableInsertions} insertion(s) available for this listing.`
      );
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const variationId =
        product?.ProductsVariantions?.[0]?._id?.toString?.() ??
        product?.ProductsVariantions?.[0]?._id ??
        null;
      const sellerCompanyId =
        product?.SellerCompanyId?._id?.toString?.() ??
        product?.SellerCompanyId?.toString?.() ??
        "";
      if (!variationId || !sellerCompanyId) {
        setError("Required product metadata missing to create order.");
        return;
      }
      await axios.post(
        "inventory/buy",
        {
          sellerCompanyId,
          OrderData: [
            {
              ProductId: product._id,
              ProductsVariantionId: variationId,
              ProductQty: Number(timelineToBuy) || 1,
              BoughtSeconds: Number(boughtSeconds) || 1,
              timelineToBought: Number(timelineToBuy) || 1,
              unitsToBebought: Number(unitsToBuy) || 1,
              Boughtdates: [],
            },
          ],
        },
        { withCredentials: true },
      );
      navigate("/admindashboard/orders?source=bw_procurement");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to create purchase");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(INVENTORY_LIST_PATH)}>
          Back to inventory
        </Button>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 920, mx: "auto" }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(INVENTORY_LIST_PATH)}
        sx={{ mb: 2, textTransform: "none" }}
      >
        Back to BXI inventory
      </Button>
      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #EDEFF2", p: 3 }}>
        <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: "22px", mb: 1 }}>
          {isDigitalRoute ? "Digital & Hoarding Media Buying" : "Media Buying"}
        </Typography>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#6B7A99", mb: 3 }}>
          {product?.ProductName}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
          <TextField
            type="number"
            size="large"
            label="Insertions to Buy"
            placeholder={`Min ${minUnits}`}
            value={unitsToBuy}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d+$/.test(val)) {
                setUnitsToBuy(val);
              }
            }}
            error={exceedsAvailable}
            helperText={
              exceedsAvailable
                ? `Exceeds available insertions (${availableInsertions})`
                : availableInsertions > 0
                ? `Available: ${availableInsertions} · Min: ${minUnits}`
                : `Min: ${minUnits}`
            }
            inputProps={{
              min: minUnits,
              max: availableInsertions > 0 ? availableInsertions : undefined,
              style: { textAlign: "center" },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "#fff",
                boxShadow: "0px 4px 8px rgba(220,220,220,1)",
              },
              "& fieldset": { border: "none" },
            }}
          />
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontFamily: "Poppins", fontSize: "16px", fontWeight: 600 }}>
            Total
          </Typography>
          <Typography sx={{ fontFamily: "Poppins", fontSize: "18px", fontWeight: 700, color: "#2261a2" }}>
            <CommaSeprator Price={totalPrice} />
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "12px", color: "#6B7A99", mt: 0.5 }}>
          TC total includes BXI-token additional cost. INR additional cost applies at order generation.
        </Typography>

        {additionalCostRows.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "#156DB6", mb: 0.75 }}>
              Additional Cost
            </Typography>
            {additionalCostRows.map((row, idx) => (
              <Typography
                key={row.key}
                sx={{ fontFamily: "Poppins", fontSize: "12px", color: "#6B7A99", mb: 0.35 }}
              >
                {idx + 1}. {row.reason} · {row.applicability} · GST {row.gstPct}% · {row.currencyType}
                {" "}
                <CommaSeprator Price={row.baseAmount} />
                {row.gstPct > 0 ? (
                  <>
                    {" + GST "}
                    <CommaSeprator Price={row.gstAmount} />
                    {" = "}
                    <CommaSeprator Price={row.totalAmount} />
                  </>
                ) : null}
              </Typography>
            ))}
          </Box>
        ) : null}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button onClick={() => navigate(INVENTORY_LIST_PATH)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={submitting || exceedsAvailable}
            sx={{ textTransform: "none", bgcolor: "#001219", "&:hover": { bgcolor: "#17313a" } }}
          >
            {submitting ? "Creating..." : "Create Order"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

